<template>
  <div :class="['e-date-picker', ...rootClasses]" ref="rootEl" v-if="isVisible">
    <div class="e-date-picker__wrapper">
      <div
        class="e-date-picker__main"
        :class="[{ active: deviceType.includes('desktop') && dateActive }]"
        @click="toggleDate"
        tabindex="0"
      >
        <div class="e-date-picker__placeholder" :class="[{ 'as-label': dateActive || hasValue }]">
          <span
            class="e-date-picker__label"
            v-html="dateActive || hasValue ? label : utils.string.ifEmpty(placeholder, label)"
          />
          <span class="e-date-picker__required" v-if="isRequired">*</span>
        </div>
        <div class="e-date-picker__value">{{ selectedDate }}</div>
        <Icon name="down" size="xxs" class="e-date-picker__down" />
        <Icon
          name="close"
          size="xxs"
          class="e-date-picker__clear"
          v-if="hasValue"
          @click.stop="onClear"
        />
      </div>
      <div class="e-date-picker__side" v-if="$slots.default">
        <slot />
      </div>
      <transition name="options-ani">
        <div
          class="e-date-picker__day"
          tabindex="-1"
          @blur="onDateBlur"
          v-if="deviceType.includes('desktop') && dateActive"
        >
          <datepicker-popup
            ref="datepickerPopupRef"
            :selected="selectedDate"
            :weeksText="weeksData"
            :monthsText="monthsData"
            :startData="startData"
            :endData="endData"
            @change="onDateOptionClick"
          />
          <div
            class="overflowBg"
            @click="onDateBlur"
            v-if="deviceType.includes('desktop') && dateActive"
          ></div>
        </div>
      </transition>
    </div>
    <form-el-msg :msg="msg" />
    <modal
      ref="dateModalRef"
      class="e-date-picker__phone-modal"
      animation="bottom-slide-in"
      :fire-gtm="false"
      closable
    >
      <datepicker-popup
        ref="datepickerPopupRefMobile"
        :selected="selectedDate"
        :weeksText="weeksData"
        :monthsText="monthsData"
        :startData="startData"
        :endData="endData"
        @change="onDateOptionClick"
      />
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import useDevice from '@/hooks/useDevice';
import utils from '@/utils';
import { FormDateEmits, FormDateProps, FormDateRef } from '@/types/form/form-date';
import { DynamicFormContext, DynamicFormFieldValidateResult } from '@/types/form/dynamic-form';
import { ModalRef } from '@/types/elements/modal';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import FormElMsg from '@/form/other/FormElMsg.vue';
import DatepickerPopup from '@/form/FormDatepicker/DatepickerPopup.vue';

const props = defineProps<FormDateProps>();
const emit = defineEmits<FormDateEmits>();
const formContext = inject<DynamicFormContext>('formContext')!;
const { deviceState } = useDevice();

const FIELD_TYPE = 'form-date';
const rootEl = ref<HTMLDivElement | null>(null);
const datepickerPopupRef = ref<any>(null);
const datepickerPopupRefMobile = ref<any>(null);
const dateModalRef = ref<ModalRef>();

const value = ref<string>('');
const selectedDate = ref<string>('');
const isRequired = ref<boolean>(props.item?.fields?.required?.value ?? false);
const isDisabled = ref<boolean>(false);
const isVisible = ref<boolean>(true);
const focused = ref<boolean>(false);
const dateActive = ref<boolean>(false);
const msg = ref<FormElMsgData>();
let blurTimeout: any = null;

const fields = computed(() => props.item?.fields);
const label = computed(() => fields.value?.label?.value || '');
const placeholder = computed(() => fields.value?.placeholder?.value || '');
const fieldName = computed(
  () => fields.value?.fieldName?.value || fields.value?.dataName?.value || ''
);
const weeksData = computed(() => fields.value?.weeks?.fields?.data);
const monthsData = computed(() => fields.value?.month?.fields?.data);
const startDataOverride = ref<number | null>(null);
const endDataOverride = ref<number | null>(null);
const startData = computed(() => startDataOverride.value ?? fields.value?.startData?.value);
const endData = computed(() => endDataOverride.value ?? fields.value?.endData?.value);
const deviceType = computed(() => deviceState.deviceType);
const hasValue = computed(() => !utils.obj.isNoE(value.value));

const rootClasses = computed(() => [
  {
    focus: focused.value,
    'has-value': hasValue.value,
    'has-error': msg.value?.type === 'error',
    disabled: isDisabled.value,
  },
]);

const toggleDate = () => {
  if (isDisabled.value) return;
  focused.value = true;
  dateActive.value = !dateActive.value;
};

const onDateBlur = () => {
  if (blurTimeout) clearTimeout(blurTimeout);
  blurTimeout = setTimeout(() => {
    if (deviceState.deviceType.includes('desktop')) {
      dateActive.value = false;
    }
  }, 50);
};

const onDateOptionClick = (date: string) => {
  dateActive.value = false;
  focused.value = false;
  selectedDate.value = date;
  value.value = date;
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, date, { date, text: date });
};

const onClear = (e: MouseEvent) => {
  e.stopPropagation();
  if (isDisabled.value) return;
  dateActive.value = false;
  focused.value = false;
  selectedDate.value = '';
  value.value = '';
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, null, { date: null, text: '' });
};

const checkMobileMotion = async () => {
  if (!deviceState.deviceType.includes('desktop')) {
    if (dateActive.value) {
      await dateModalRef.value?.open();
      await nextTick();
    } else {
      await dateModalRef.value?.close();
    }
  } else {
    if (dateModalRef.value) {
      await dateModalRef.value?.close();
    }
  }
};

const setRequired = (required: boolean) => {
  isRequired.value = required;
};
const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};
const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};
const updateValue = (newValue?: string | null) => {
  value.value = newValue || '';
  selectedDate.value = newValue || '';
};
const setStartData = (newValue?: number | null) => {
  startDataOverride.value = newValue ?? null;
};
const setEndData = (newValue?: number | null) => {
  endDataOverride.value = newValue ?? null;
};
const getValue = () => value.value;

const validate = async (): Promise<DynamicFormFieldValidateResult> => {
  const result: DynamicFormFieldValidateResult = {
    value: value.value,
    valid: true,
    message: '',
  };
  if (!isVisible.value || !fields.value) return result;
  if (isRequired.value && utils.obj.isNoE(value.value)) {
    result.valid = false;
    result.message = fields.value?.requiredMsg?.value || 'This field is required';
    msg.value = { type: 'error', message: result.message };
    return result;
  }
  msg.value = undefined;
  return result;
};

const getRefData = (): FormDateRef => ({
  el: rootEl.value,
  type: FIELD_TYPE,
  name: fieldName.value,
  id: props.item?.id ?? '',
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  setStartData,
  setEndData,
  getValue,
  validate,
});

watch(dateActive, () => {
  checkMobileMotion();
});

watch(
  () => deviceState.deviceType,
  () => {
    checkMobileMotion();
  }
);

watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);

watch(
  () => props.item?.fields?.required?.value,
  (val) => {
    isRequired.value = val ?? false;
  }
);

onMounted(() => {
  formContext.regFormEl(getRefData());
});

onBeforeUnmount(() => {
  if (blurTimeout) clearTimeout(blurTimeout);
  formContext.unRegFormEl(getRefData());
});

defineExpose(getRefData());
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.e-date-picker {
  $c: &;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  width: 100%;
  color: var(--item-color, #000000);

  &__wrapper {
    display: flex;
    position: relative;
    z-index: 2;
  }

  &__main {
    position: relative;
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    height: 60px;
    padding: 8px 16px;
    border-radius: 4px;
    border: 1px solid var(--item-border-color, #c4c4c4);
    background: var(--item-back-color, #ffffff);
    cursor: pointer;
    outline: none;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;

    &:focus-within {
      box-shadow: 0 0 0 2px rgba(8, 49, 194, 0.1);
    }

    &.active {
      z-index: 2;
      border-color: #6b9171;
      box-shadow: 0 0 0 2px rgba(8, 49, 194, 0.1);
    }
  }

  &__placeholder {
    position: absolute;
    width: 100%;
    height: 60px;
    top: 0;
    left: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--item-color, #929292);
    font-size: 16px;
    padding: 10px 72px 10px 16px;
    transition: all 0.2s ease-in-out;

    &.as-label {
      font-size: 12px;
      height: 36px;
      opacity: 0.7;
    }
  }

  &__value {
    flex: 1;
    align-self: flex-end;
    font-family: 'Figtree', sans-serif;
    font-size: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: $black;
  }

  &__down {
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  &__clear {
    flex-shrink: 0;
    cursor: pointer;
    color: $grey;
    transition: color 0.2s ease;

    &:hover {
      color: $black;
    }
  }

  &.active,
  &.focus {
    #{$c}__down {
      transform: rotate(180deg);
    }
  }

  &__day {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: 400px;
    height: 400px;
    background-color: var(--item-curtain-back-color, #ffffff);
    color: var(--item-curtain-color, #000000);
    border: 1px solid var(--item-border-color, #c4c4c4);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 100;
  }

  &__side {
    margin-left: 16px;
    width: 65px;
    flex-shrink: 0;
    .e-site-button {
      width: 100%;
      min-width: auto !important;
      height: 60px !important;
    }
    &:empty {
      display: none;
    }
  }

  &.has-error {
    #{$c}__main {
      border-color: #dc3545;
    }
  }

  &.disabled {
    #{$c}__main {
      background-color: rgba($grey, 0.3);
      cursor: not-allowed;
    }
  }

  &__phone-modal {
    align-items: flex-end;
    .e-modal__content {
      width: 100%;
      padding: $space-40 0 0;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }
    .e-modal__close {
      top: 12px;
    }
  }

  @include tablet-landscape {
    &__side {
      width: 140px;
    }
  }
}

html.rtl {
  .e-date-picker {
    &__placeholder {
      padding: 10px 16px 10px 72px;
    }
  }
}

.overflowBg {
  position: fixed;
  z-index: 1;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
}
</style>
